import { useEffect, useState } from "react";

type useSetActiveSelectProps = {
  /** The ref of the button */
  buttonRef: React.RefObject<HTMLElement | null>;
  /** The list item elements */
  itemEls: Array<HTMLLIElement | null>;
  /** The state of the dropdown */
  isOpen: boolean;
  /** The function to set the state of the dropdown */
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** The filtered options */
  filteredOptions: { value: string; label: string }[];
  /** The function to handle the validation */
  handleSelectOption: (option: { value: string; label: string }) => void;
};

/** Custom hook to set the active index of the dropdown list. */
const useSetActiveSelect = ({
  buttonRef,
  itemEls,
  isOpen,
  setIsOpen,
  filteredOptions,
  handleSelectOption,
}: useSetActiveSelectProps) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  const handleSetActiveIndex = (index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    if (activeIndex < 0) return;
    itemEls[activeIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [activeIndex, itemEls]);

  useEffect(() => {
    if (activeIndex < 0) return;
    if (activeIndex > filteredOptions.length - 1) {
      setActiveIndex(-1);
    }
  }, [activeIndex, filteredOptions.length]);

  const closeDropdown = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const moveActiveIndex = (delta: number) => {
    if (filteredOptions.length === 0) return;

    if (activeIndex < 0) {
      setActiveIndex(delta > 0 ? 0 : filteredOptions.length - 1);

      return;
    }

    const next =
      (activeIndex + delta + filteredOptions.length) % filteredOptions.length;

    setActiveIndex(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveActiveIndex(1);
        break;

      case "ArrowUp":
        e.preventDefault();
        moveActiveIndex(-1);
        break;

      case "Enter":
        e.preventDefault();
        if (activeIndex < 0) return;
        if (!filteredOptions[activeIndex]) return;

        handleSelectOption(filteredOptions[activeIndex]);
        closeDropdown();
        buttonRef.current?.blur();

        break;

      case "Escape":
        closeDropdown();
        break;
    }
  };

  return {
    activeIndex,
    handleSetActiveIndex,
    handleKeyDown,
  };
};

export default useSetActiveSelect;
